import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  Fade,
  Zoom,
  Box,
} from "@mui/material";
import { COLORS, size } from "../../utils";

export default function LandingPage({ headerHeight = 74 }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoaded(true), 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      sx={{
        height: `calc(100vh - ${headerHeight}px)`,
        backgroundColor: "#ffffff",
        padding: isMobile ? size({ p: 4 }) : size({ p: 12 }),
        position: "relative",
        overflow: "hidden",
        textAlign: "center",
      }}
    >
      {/* Hero Headline */}
      <Fade in={loaded} timeout={1000}>
        <Grid item sx={{ mb: isMobile ? "1.5rem" : "3rem" }}>
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: isMobile ? size({ p: 8 }) : size({ p: 3.5 }),
              fontWeight: 900,
              lineHeight: 1.2,
              background: `linear-gradient(90deg, ${COLORS.C1}, ${COLORS.C2})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textShadow: "0px 4px 10px rgba(0,0,0,0.05)",
              animation: "headlinePulse 2s ease-in-out infinite",
              mt: "-5rem",
            }}
          >
            Empowering the Next Generation of Tech Innovators
          </Typography>
        </Grid>
      </Fade>

      {/* Subheadline */}
      <Fade in={loaded} timeout={1200}>
        <Grid item sx={{ mb: isMobile ? "1rem" : "2rem" }}>
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: isMobile ? size({ p: 4 }) : size({ p: 1.7 }),
              fontWeight: 600,
              color: COLORS.C1,
              animation: "slideUp 1.5s ease-out",
            }}
          >
            Learn. Build. Innovate — With Genesys Academy
          </Typography>
        </Grid>
      </Fade>

      {/* Description */}
      <Fade in={loaded} timeout={1400}>
        <Grid
          item
          sx={{
            mb: isMobile ? "2rem" : "3rem",
            maxWidth: isMobile ? "90%" : "100%",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Archia",
              fontSize: isMobile ? size({ p: 2.5 }) : size({ p: 1.1 }),
              fontWeight: 400,
              color: `${COLORS.C1}A0`,
              lineHeight: 1.8,
              animation: "slideUp 1.8s ease-out",
            }}
          >
            Trusted destination since 2015 for future-ready learning in
            Programming, AI, Data Science, Computer Vision, IoT Systems, and
            other Emerging Technologies. From certification courses, hands-on
            workshops, internships, project guidance, and research support — we
            empower students, professionals, and researchers with practical
            skills to thrive in today’s competitive digital world.
            <br />
            <br />A Place Where Innovation Meets Expertise— A Platform Built to
            Help You Grow, Create, and Achieve Your Goals.
          </Typography>
        </Grid>
      </Fade>

      {/* CTA Button */}
      <Zoom in={loaded} timeout={1600}>
        <Grid item>
          <Box
            sx={{
              width: isMobile ? "70px" : "105px", // container size
              height: isMobile ? "70px" : "105px",
              borderRadius: "50%",
              boxShadow: "0 0px 5px 0px rgba(0,0,0,0.3)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px) scale(1.1)",
                filter: "drop-shadow(0px 8px 20px rgba(0,0,0,0.3))",
                boxShadow: "0 0px 6px 0px rgba(0,0,0,0.3)",
              },
            }}
            onClick={() => {
              const nextSection = document.getElementById("courses-section");
              if (nextSection) {
                nextSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
          >
            <Box
              component="img"
              src="/assets/images/home/down-arrow.svg"
              alt="Explore Courses"
              sx={{
                width: isMobile ? "40px" : "90px",
                height: "auto",
              }}
            />
          </Box>
        </Grid>
      </Zoom>

      {/* Floating abstract tech shapes */}
      <div
        style={{
          position: "absolute",
          top: "-80px",
          left: "-80px",
          width: "200px",
          height: "200px",
          borderRadius: "50%",
          backgroundColor: `${COLORS.C1}10`,
          animation: "float1 10s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: "-100px",
          right: "-100px",
          width: "250px",
          height: "250px",
          borderRadius: "50%",
          backgroundColor: `${COLORS.C2}10`,
          animation: "float2 12s ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          top: "30%",
          right: "-50px",
          width: "100px",
          height: "100px",
          borderRadius: "50%",
          backgroundColor: `${COLORS.C3}15`,
          animation: "float3 14s ease-in-out infinite",
        }}
      />

      {/* Animations */}
      <style>
        {`
          @keyframes float1 {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(20px); }
          }
          @keyframes float2 {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-25px); }
          }
          @keyframes float3 {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(15px); }
          }
          @keyframes headlinePulse {
            0%, 100% { transform: scale(1); }
            50% { transform: scale(1.02); }
          }
          @keyframes slideUp {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </Grid>
  );
}
